import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ReleaseNotesEvents, type WhatsNewSurface } from '@/analytics/release-notes-events';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { APP_NAME } from '@/config/app';

import type { NotesLoader } from './content';
import { RELEASE_NOTES, type ReleaseNotesEntry } from './manifest';
import { ReleaseNotesList } from './release-notes-list';
import { markLatestSeen } from './seen-store';

interface WhatsNewDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Which entry point opened it (analytics). The page has its own surface
     *  and does not render this dialog. */
    surface: Exclude<WhatsNewSurface, 'page'>;
    entries?: readonly ReleaseNotesEntry[];
    load?: NotesLoader;
}

/** The What's-new modal. Opening it clears the unread dot. "Open as a page"
 *  opens /whats-new in a new tab so an editing session is never navigated away. */
export function WhatsNewDialog({ open, onOpenChange, surface, entries = RELEASE_NOTES, load }: WhatsNewDialogProps) {
    const { t } = useTranslation();
    const latest = entries[0]?.version;

    useEffect(() => {
        if (!open || latest == null) return;
        markLatestSeen(entries);
        ReleaseNotesEvents.opened(surface, latest);
        // effect is keyed on the newest version, so it fires once per open even if a caller passes a fresh array
    }, [open, latest, surface]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>{t('whatsNew.title')}</DialogTitle>
                    <DialogDescription>{t('whatsNew.description', { appName: APP_NAME })}</DialogDescription>
                </DialogHeader>
                <div className="-mx-6 min-h-0 flex-1 overflow-y-auto px-6">
                    <ReleaseNotesList entries={entries} load={load} />
                </div>
                <a
                    href="/whats-new"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="self-start text-sm font-medium underline underline-offset-4"
                >
                    {t('whatsNew.openAsPage')}
                </a>
            </DialogContent>
        </Dialog>
    );
}
